import { RefCallback, useMemo, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { noop } from 'lodash-es';
import { TMDXFile } from './UploadedFile';
import { Components } from './config';
import { FileInputStyle } from './types';
import { getFileRejectionErrorMessages } from './FileInput.helpers';
import { mapFilesBeforeUpload } from '@/utils/helpers';

export interface FileInputProps extends DropzoneOptions {
  files: TMDXFile[];
  placeholder: string;
  isInvalid?: boolean;
  errorMessage?: string;
  inputRef?: RefCallback<HTMLDivElement>;
  showMaxSize?: boolean;
  buttonType?: FileInputStyle;
  showRedactedBadge?: boolean;
  redactedBadgeHintText?: string;
  onFilesChange: (files: TMDXFile[]) => void;
  onFileDelete: (file: TMDXFile) => void;
}

export const FileInput = ({
  files,
  placeholder,
  showMaxSize,
  inputRef,
  isInvalid,
  errorMessage,
  buttonType = FileInputStyle.regular,
  showRedactedBadge,
  redactedBadgeHintText,
  onFilesChange,
  onFileDelete,
  disabled = false,
  ...dropZoneProps
}: FileInputProps) => {
  const [errors, setErrors] = useState<string[]>([]);
  const remainingFiles =
    dropZoneProps.maxFiles !== undefined ? Math.max(dropZoneProps.maxFiles - files.length, 0) : undefined;
  const { getRootProps, getInputProps } = useDropzone({
    ...dropZoneProps,
    maxFiles: remainingFiles,
    disabled,
    onDrop: (acceptedFiles: File[], fileRejections) => {
      if (disabled) return;

      if (acceptedFiles.length) {
        const tmdxFiles: TMDXFile[] = mapFilesBeforeUpload(acceptedFiles);
        onFilesChange(tmdxFiles);
      }

      if (fileRejections.length) {
        setErrors(
          getFileRejectionErrorMessages(fileRejections, {
            maxSize: dropZoneProps.maxSize,
            maxFiles: dropZoneProps.maxFiles,
          }),
        );
      } else {
        setErrors([]);
      }
    },
  });

  const RenderedComponent = useMemo(() => {
    return buttonType === FileInputStyle.dashed ? Components.dashedButton : Components.button;
  }, [buttonType]);

  const showInput = () => {
    if (disabled) return false;
    if (dropZoneProps.maxFiles) return dropZoneProps.maxFiles > files.length;
    return !!dropZoneProps.multiple || !files.length;
  };

  const errorMessages = useMemo(() => {
    return [errorMessage, ...errors].filter((e) => !!e).join(', ');
  }, [errors, errorMessage]);

  const invalid = !!isInvalid || !!errorMessages.length;

  return (
    <div className="flex flex-col gap-1 min-w-0 w-full">
      <div
        className={`${RenderedComponent.wrapperClassNames} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        ref={inputRef}
      >
        {files.map((f) => (
          <RenderedComponent.FilesComponent
            key={f.id}
            file={f}
            onDelete={disabled ? noop : onFileDelete}
            showDeleteBtn
            showHint={showRedactedBadge}
            hintText={redactedBadgeHintText}
          />
        ))}
        {showInput() && (
          <RenderedComponent.ButtonComponent
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            placeholder={placeholder}
            isInvalid={invalid}
          />
        )}
      </div>
      {invalid && <span className="text-tiny text-danger">{errorMessages}</span>}
    </div>
  );
};
